import { Select, Space, Typography, App } from 'antd';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { getAiTasks, updateAiTasks, type AiTaskRow } from '@/api/aiTasks';
import { getAiModels, type AiModel } from '@/api/aiModels';

export function FunctionModelSection() {
  const { message } = App.useApp();
  const qc = useQueryClient();
  const tasksQ = useQuery({ queryKey: ['ai-tasks'], queryFn: getAiTasks });
  const modelsQ = useQuery({ queryKey: ['ai-models'], queryFn: getAiModels });
  const models = (modelsQ.data ?? []).filter((m) => m.enabled);

  const refresh = () => qc.invalidateQueries({ queryKey: ['ai-tasks'] });

  async function setDefault(model: string | null) {
    try { await updateAiTasks({ default_model: model }); refresh(); }
    catch (e) { message.error((e as Error).message); }
  }
  async function bind(task: string, model: string | null) {
    try { await updateAiTasks({ bindings: { [task]: model } }); refresh(); }
    catch (e) { message.error((e as Error).message); }
  }

  const opts = (list: AiModel[]) => list.map((m) => ({ value: m.model, label: `${m.model} (${m.provider})` }));
  const def = tasksQ.data?.default_model ?? null;

  return (
    <div style={{ maxWidth: 640 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
        <Typography.Text strong style={{ width: 160 }}>Default model</Typography.Text>
        <Select
          style={{ flex: 1 }}
          loading={tasksQ.isLoading || modelsQ.isLoading}
          value={def ?? undefined}
          placeholder="None — features need a per-function or default model"
          allowClear
          onChange={(v) => setDefault(v ?? null)}
          options={opts(models)}
        />
      </div>
      {(tasksQ.data?.tasks ?? []).map((t: AiTaskRow) => {
        const eligible = t.vision ? models.filter((m) => m.supportsVision) : models;
        return (
          <div key={t.key} style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
            <span style={{ width: 160 }}>
              {t.label}
              {t.key === 'extract_text' && <Typography.Text type="secondary"> (inert)</Typography.Text>}
            </span>
            <Select
              style={{ flex: 1 }}
              value={t.model ?? undefined}
              placeholder={`Default${def ? ` (${def})` : ''}`}
              allowClear
              onChange={(v) => bind(t.key, v ?? null)}
              options={opts(eligible)}
            />
          </div>
        );
      })}
      <Space>
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          A function with no model uses the default. Changes save immediately.
        </Typography.Text>
      </Space>
    </div>
  );
}
